import { useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, Button, Input, Field, Badge } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

const TABLE_TITLES: Record<string, string> = {
  campaigns: 'Campaigns (DynamoDB)', users: 'Users', user_clicks: 'User Clicks',
  user_click_ip: 'User Click IP', blacklist: 'Blacklist', user_attempt: 'User Attempt',
};
const n = (v: unknown) => Number(v) || 0;
const PLACEHOLDER = 'https://via.placeholder.com/300x300?text=No+Image';

interface Product {
  images?: string; image?: string; image_link?: string; name?: string; title?: string;
  price?: number; salesPrice?: number; sale_price?: number; currency?: string;
  itemId?: string; id?: string; sku?: string; category?: string; seen?: boolean;
  scores?: { purchase?: number; clicks?: number; cart?: number; product?: number };
  purchase?: number; clicks?: number; cart?: number; product?: number;
  url?: string; link?: string; urlwithtracking?: string; url_with_tracking?: string;
  linkId?: string; link_id?: string;
}

function ProductCard({ p, i }: { p: Product; i: number }) {
  const img = p.images || p.image || p.image_link || PLACEHOLDER;
  const name = p.name || p.title || 'Unnamed Product';
  const price = n(p.price), sale = n(p.salesPrice ?? p.sale_price);
  const cur = p.currency || 'USD';
  const s = p.scores || {};
  const url = p.url || p.link || '#';
  const urlT = p.urlwithtracking || p.url_with_tracking || url;
  const linkId = p.linkId || p.link_id;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-700 to-slate-600 text-white text-xs">
        <span className="font-semibold">Product #{i + 1}</span>
        <Badge tone={p.seen ? 'green' : 'neutral'}>{p.seen ? 'Seen' : 'Not Seen'}</Badge>
      </div>
      <img src={img} alt={name} className="w-full h-40 object-contain bg-white p-2" onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }} />
      <div className="p-3 text-xs space-y-1.5">
        <div className="font-semibold text-ink-900 line-clamp-2">{name.slice(0, 50)}</div>
        <div>
          {sale > 0 && sale < price
            ? <><span className="line-through text-slate-400">{cur} {price.toFixed(2)}</span> <span className="text-rose-600 font-bold">{cur} {sale.toFixed(2)}</span></>
            : <span className="text-slate-700 font-semibold">{cur} {price.toFixed(2)}</span>}
        </div>
        <div className="text-slate-400">Item ID: {p.itemId || p.id || 'N/A'}</div>
        <div className="text-slate-400">SKU: {p.sku || 'N/A'}</div>
        <div className="text-slate-400">Category: {p.category || 'Uncategorized'}</div>
        <div className="rounded-lg bg-surface-muted p-2 grid grid-cols-4 gap-1 text-center">
          <div><div className="text-rose-600 font-bold">{n(s.purchase ?? p.purchase).toLocaleString()}</div><div className="text-[10px] text-slate-400">Purchase</div></div>
          <div><div className="text-sky-600 font-bold">{n(s.clicks ?? p.clicks).toLocaleString()}</div><div className="text-[10px] text-slate-400">Clicks</div></div>
          <div><div className="text-amber-600 font-bold">{n(s.cart ?? p.cart).toLocaleString()}</div><div className="text-[10px] text-slate-400">Cart</div></div>
          <div><div className="text-violet-600 font-bold">{n(s.product ?? p.product).toLocaleString()}</div><div className="text-[10px] text-slate-400">Product</div></div>
        </div>
        <div className="flex flex-col gap-1 pt-1">
          <a className="text-center rounded-md border border-border py-1 hover:bg-slate-50" href={url} target="_blank" rel="noreferrer">Link</a>
          <a className="text-center rounded-md bg-sky-500 text-white py-1 hover:bg-sky-600" href={urlT} target="_blank" rel="noreferrer">Link with Tracking</a>
          {linkId && <a className="text-center rounded-md bg-brand-600 text-white py-1 hover:bg-brand-700" href={`https://trackingredir.com/r/${linkId}`} target="_blank" rel="noreferrer">Redirector Link</a>}
        </div>
      </div>
    </Card>
  );
}

export default function Debug() {
  const [rUser, setRUser] = useState('');
  const [rIp, setRIp] = useState('');
  const [vCampaign, setVCampaign] = useState('');
  const [vUser, setVUser] = useState('');
  const [vIp, setVIp] = useState('');
  const [viewData, setViewData] = useState<Record<string, unknown> | null>(null);
  const [viewErr, setViewErr] = useState<string | null>(null);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [recs, setRecs] = useState<{ url: string; data?: Record<string, unknown>; error?: string } | null>(null);
  const [recLoading, setRecLoading] = useState(false);

  async function reset() {
    if (!rUser.trim() && !rIp.trim()) { alert('Please enter at least User ID or User IP'); return; }
    if (!confirm('Are you sure you want to reset the data? This action cannot be undone.')) return;
    setBusy('reset'); setResetMsg(null);
    try {
      const r = await api.post<ApiResponse<Record<string, boolean>>>('/debug/reset', { userId: rUser.trim() || null, userIP: rIp.trim() || null });
      if (r.success) {
        const done = Object.entries(r.data || {}).map(([k, v]) => `${k}: ${v ? 'ok' : '—'}`).join(' · ');
        setResetMsg(`Data reset successfully. ${done}`);
        setRUser(''); setRIp(''); setViewData(null); setRecs(null);
      } else setResetMsg(r.error || r.message || 'Unknown error');
    } catch (e) { setResetMsg(e instanceof ApiError ? e.message : 'Failed'); } finally { setBusy(null); }
  }

  async function view() {
    if (!vCampaign.trim() && !vUser.trim() && !vIp.trim()) { alert('Please enter at least Campaign ID, User ID or User IP'); return; }
    setBusy('view'); setViewErr(null); setViewData(null); setRecs(null);
    try {
      const r = await api.post<ApiResponse<Record<string, unknown>>>('/debug/view', { campaignId: vCampaign.trim() || null, userId: vUser.trim() || null, userIP: vIp.trim() || null });
      if (r.success) {
        setViewData(r.data || {});
        if (vCampaign.trim() && vUser.trim()) fetchRecommendations(vCampaign.trim(), vUser.trim());
      } else setViewErr(r.error || r.message || 'Unknown error');
    } catch (e) { setViewErr(e instanceof ApiError ? e.message : 'Failed'); } finally { setBusy(null); }
  }

  async function fetchRecommendations(campaignId: string, userId: string) {
    const url = `https://audience.pixeldmp.com/recommendation/${campaignId}/${userId}`;
    setRecLoading(true); setRecs({ url });
    try {
      const res = await fetch(url, { signal: AbortSignal.timeout(30000) });
      const data = await res.json();
      setRecs({ url, data });
    } catch (e) {
      setRecs({ url, error: e instanceof Error ? e.message : 'Failed to fetch recommendations' });
    } finally { setRecLoading(false); }
  }

  const products: Product[] = (() => {
    const d = recs?.data as Record<string, unknown> | undefined;
    if (!d) return [];
    return (d.items || d.products || d.recommendations || []) as Product[];
  })();

  const presentTables = Object.keys(TABLE_TITLES).filter((k) => viewData && viewData[k]);

  return (
    <Layout title="Debug Tools">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-ink-900 mb-3">Reset Data</h2>
          <div className="space-y-3">
            <Field label="User ID"><Input value={rUser} onChange={(e) => setRUser(e.target.value)} placeholder="Enter User ID" /></Field>
            <Field label="User IP"><Input value={rIp} onChange={(e) => setRIp(e.target.value)} placeholder="Enter User IP" /></Field>
            <Button variant="danger" onClick={reset} disabled={busy === 'reset'}>{busy === 'reset' ? '…' : '✕ Reset'}</Button>
          </div>
          {resetMsg && <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3 py-2">{resetMsg}</div>}
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold text-ink-900 mb-3">View Data</h2>
          <div className="space-y-3">
            <Field label="Campaign ID"><Input value={vCampaign} onChange={(e) => setVCampaign(e.target.value)} placeholder="Enter Campaign ID" /></Field>
            <Field label="User ID"><Input value={vUser} onChange={(e) => setVUser(e.target.value)} placeholder="Enter User ID" /></Field>
            <Field label="User IP"><Input value={vIp} onChange={(e) => setVIp(e.target.value)} placeholder="Enter User IP" /></Field>
            <Button onClick={view} disabled={busy === 'view'}>{busy === 'view' ? '…' : '👁 View'}</Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">With Campaign ID + User ID, it also fetches the recommendations.</p>
        </Card>
      </div>

      {viewErr && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">Error: {viewErr}</div>}

      {viewData && (presentTables.length > 0 ? (
        <div className="space-y-4 mb-6">
          {presentTables.map((k) => (
            <Card key={k} className="overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border text-sm font-semibold text-ink-900">{TABLE_TITLES[k]}</div>
              <pre className="p-4 text-xs bg-surface-muted overflow-auto max-h-80">{JSON.stringify(viewData[k], null, 2)}</pre>
            </Card>
          ))}
        </div>
      ) : <div className="mb-6 rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-sm px-4 py-3">No data found</div>)}

      {recs && (
        <Card className="overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border">
            <div className="text-sm font-semibold text-ink-900">Recommendations API {recLoading && <span className="text-slate-400 font-normal">— Loading…</span>}</div>
            <code className="text-[11px] text-slate-400 break-all">{recs.url}</code>
          </div>
          {recs.error ? (
            <div className="p-4 text-sm text-rose-700">{recs.error}</div>
          ) : recs.data ? (
            <div className="p-4">
              {products.length > 0 ? (
                <>
                  <h4 className="text-sm font-semibold text-ink-900 mb-3">★ Recommended Products ({products.length})</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-5">
                    {products.map((p, i) => <ProductCard key={i} p={p} i={i} />)}
                  </div>
                </>
              ) : <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-sm px-4 py-3 mb-4">No products found in the recommendations response.</div>}
              <div className="text-xs font-semibold text-slate-500 mb-1">Raw JSON Response</div>
              <pre className="rounded-lg bg-slate-800 text-slate-100 p-4 text-[11px] overflow-auto max-h-[500px]">{JSON.stringify(recs.data, null, 2)}</pre>
            </div>
          ) : null}
        </Card>
      )}
    </Layout>
  );
}
